<template>
  <div v-if="current || types.length || idle" class="pause-control d-flex align-items-center mr-1">
    <div
      v-if="idle"
      class="idle-pill mr-1"
      :title="$t('pause.idleTooltip', { time: clock(idleSeconds) })"
    >
      <feather-icon icon="ClockIcon" size="15" />
      <span class="idle-pill__label">{{ $t('pause.idleBadge') }}</span>
      <span class="idle-pill__time">{{ clock(idleSeconds) }}</span>
    </div>

    <operator-notice
      v-if="idle"
      variant="warning"
      icon="ClockIcon"
      :title="$t('pause.noticeIdleTitle')"
      :text="$t('pause.noticeIdleText', { time: clock(idleSeconds) })"
    />

    <operator-notice
      v-if="overdue"
      variant="danger"
      icon="AlertTriangleIcon"
      :title="$t('pause.noticeOverdueTitle')"
      :text="$t('pause.noticeOverdueText', { pause: current.type_name, time: clock(-remaining) })"
    >
      <b-button size="sm" variant="danger" :disabled="busy" @click="back">
        {{ $t('pause.back') }}
      </b-button>
    </operator-notice>

    <div
      v-if="current"
      class="pause-pill"
      :class="{ 'pause-pill--overdue': overdue, 'pause-pill--warning': nearEnd }"
    >
      <div class="pause-pill__icon">
        <feather-icon :icon="overdue ? 'AlertTriangleIcon' : 'CoffeeIcon'" size="18" />
      </div>

      <div class="pause-pill__info">
        <span class="pause-pill__status">{{ $t('pause.onPause') }}</span>
        <strong class="pause-pill__name" :title="current.type_name">{{ current.type_name }}</strong>
      </div>

      <div class="pause-pill__clock">
        <span class="pause-pill__value">{{ clock(elapsed) }}</span>
        <span class="pause-pill__caption">{{ $t('pause.elapsedShort') }}</span>
      </div>

      <div class="pause-pill__clock pause-pill__clock--main">
        <span class="pause-pill__value">{{ overdue ? '+' + clock(-remaining) : clock(remaining) }}</span>
        <span class="pause-pill__caption">{{ overdue ? $t('pause.overdue') : $t('pause.remaining') }}</span>
      </div>

      <b-button
        size="sm"
        :variant="overdue ? 'light' : 'primary'"
        :disabled="busy"
        @click="back"
      >
        <feather-icon icon="CornerUpLeftIcon" size="14" class="mr-50" />{{ $t('pause.back') }}
      </b-button>

      <div class="pause-pill__progress">
        <span :style="{ width: progress + '%' }" />
      </div>
    </div>

    <b-button
      v-else-if="types.length"
      size="sm"
      variant="light"
      @click="openStartModal"
    >
      <feather-icon icon="CoffeeIcon" size="15" class="mr-50" />{{ $t('pause.button') }}
    </b-button>

    <b-modal
      id="modal-pause-start"
      :title="$t('pause.modalTitle')"
      :ok-title="$t('pause.confirm')"
      :cancel-title="$t('common.cancel')"
      :ok-disabled="!selectedTypeId || busy"
      centered
      scrollable
      @ok.prevent="startSelected"
    >
      <p class="text-muted mb-1">{{ $t('pause.chooseType') }}</p>

      <input
        v-if="types.length > SEARCH_FROM"
        v-model="typeSearch"
        type="search"
        class="form-control mb-1"
        :placeholder="$t('pause.searchPlaceholder')"
      />

      <div class="pause-options">
        <button
          v-for="type in filteredTypes"
          :key="type.id"
          type="button"
          class="pause-option"
          :class="{ 'pause-option--selected': selectedTypeId === type.id }"
          :disabled="!type.can_start"
          @click="selectedTypeId = type.id"
        >
          <span class="pause-option__radio" />
          <span class="pause-option__text">
            <strong>{{ type.name }}</strong>
            <small>{{ typeMeta(type) }}</small>
          </span>
        </button>
        <p v-if="!filteredTypes.length" class="text-muted text-center my-1">
          {{ $t('pause.noMatch') }}
        </p>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { BButton, BModal } from "bootstrap-vue";
import ApiErrorMixin from "@/mixins/ApiErrorMixin";
import { serverNow } from "@/libs/serverTime";
import { formatClock } from "@/utils/duration";
import { getLastInteractionAt } from "@/libs/activityTracker";
import OperatorNotice from "./OperatorNotice.vue";

const EVENT = ".SupervisionChanged";
// A busca só aparece quando há tipos suficientes para precisar dela.
const SEARCH_FROM = 6;

export default {
  components: { BButton, BModal, OperatorNotice },
  mixins: [ApiErrorMixin],
  data() {
    return {
      current: null,
      types: [],
      selectedTypeId: null,
      typeSearch: "",
      SEARCH_FROM,
      idleLimitSeconds: null,
      busy: false,
      now: serverNow(),
      localNow: Date.now(),
      tickTimer: null,
      idleLimitTimer: null,
      channelName: null,
      listener: null,
    };
  },
  computed: {
    userId() {
      const user = this.$store.state.user;
      return user && user.id ? user.id : null;
    },
    nodeId() {
      const node = this.$store.state.selected_node;
      return node && node.id ? node.id : null;
    },
    elapsed() {
      if (!this.current) return 0;
      return Math.max(0, Math.floor((this.now - Date.parse(this.current.started_at)) / 1000));
    },
    // ceil: com o decorrido arredondado para baixo, os dois somam o tempo máximo
    // (00:07 + 09:53 = 10:00), em vez de perder 1s em cada contador.
    remaining() {
      if (!this.current) return 0;
      return Math.ceil((Date.parse(this.current.expected_end_at) - this.now) / 1000);
    },
    overdue() {
      return !!this.current && this.remaining < 0;
    },
    // Quanto do tempo máximo da pausa já passou (0 a 100), para a barra de progresso.
    progress() {
      if (!this.current) return 0;
      return Math.min(100, (this.elapsed / (this.current.max_minutes * 60)) * 100);
    },
    // Último minuto da pausa: o contador troca de cor antes de estourar.
    nearEnd() {
      return !!this.current && this.remaining >= 0 && this.remaining <= 60;
    },
    filteredTypes() {
      const term = this.typeSearch.trim().toLowerCase();
      return term ? this.types.filter((t) => t.name.toLowerCase().includes(term)) : this.types;
    },
    // Tempo desta aba sem o usuário mexer; a supervisão enxerga o mesmo.
    // Usa o relógio local dos dois lados: a hora da última interação é local,
    // e misturar com a hora do servidor deslocaria o resultado se os relógios divergirem.
    idleSeconds() {
      return Math.max(0, Math.floor((this.localNow - getLastInteractionAt()) / 1000));
    },
    idle() {
      return !this.current && this.idleLimitSeconds !== null && this.idleSeconds >= this.idleLimitSeconds;
    },
    operatorStatus() {
      if (this.current) return "paused";
      return this.idle ? "idle" : "operating";
    },
  },
  watch: {
    // A bolinha do avatar (no Navbar) mostra este estado.
    operatorStatus: {
      immediate: true,
      handler(status) {
        this.$store.commit("setOperatorStatus", status);
      },
    },
    userId: {
      immediate: true,
      handler(id) {
        this.unsubscribe();
        if (id) {
          this.loadCurrent();
          this.loadTypes();
          this.loadIdleLimit();
          this.subscribe(id);
        }
      },
    },
    // O botão só aparece se o nó atual tem tipos de pausa cadastrados.
    nodeId: {
      immediate: true,
      handler(id) {
        this.types = [];
        if (id && this.userId) {
          this.loadTypes();
          this.loadIdleLimit();
        }
      },
    },
  },
  mounted() {
    this.tickTimer = setInterval(() => {
      this.now = serverNow();
      this.localNow = Date.now();
    }, 1000);
    // O limite é configurado no admin do nó: renova para não ficar com valor antigo.
    this.idleLimitTimer = setInterval(() => this.loadIdleLimit(), 60000);
  },
  beforeDestroy() {
    clearInterval(this.tickTimer);
    clearInterval(this.idleLimitTimer);
    this.$store.commit("setOperatorStatus", "operating");
    this.unsubscribe();
  },
  methods: {
    clock: formatClock,
    typeMeta(type) {
      const minutes = this.$t("pause.maxMinutes", { minutes: type.max_minutes });
      if (!type.can_start) return `${minutes} · ${this.$t("pause.limitReached")}`;
      if (type.remaining_count === null) return `${minutes} · ${this.$t("pause.unlimited")}`;
      return `${minutes} · ${this.$t("pause.remainingCount", { count: type.remaining_count })}`;
    },
    loadCurrent() {
      return this.$http
        .get(`${this.$store.state.api}pauses/current`)
        .then((r) => {
          this.current = r.data.data;
        })
        .catch(() => {});
    },
    loadIdleLimit() {
      return this.$http
        .get(`${this.$store.state.api}presence/me`)
        .then((r) => {
          this.idleLimitSeconds = r.data.idle_after_minutes * 60;
          if (localStorage.getItem("debugActivity") === "1") {
            console.log("[idle] limite do nó:", this.idleLimitSeconds, "s");
          }
        })
        .catch(() => {});
    },
    loadTypes() {
      // Respostas fora de ordem (troca de nó, abertura do modal) não podem sobrescrever a mais nova.
      const request = (this.typesRequest = (this.typesRequest || 0) + 1);

      return this.$http
        .get(`${this.$store.state.api}pauses/available`)
        .then((r) => {
          if (request === this.typesRequest) this.types = r.data.data;
        })
        .catch(() => {});
    },
    openStartModal() {
      this.selectedTypeId = null;
      this.typeSearch = "";
      this.$bvModal.show("modal-pause-start");
      // Atualiza os saldos de cada tipo enquanto o modal abre.
      this.loadTypes();
    },
    startSelected() {
      if (!this.selectedTypeId) return;

      this.busy = true;
      return this.$http
        .post(`${this.$store.state.api}pauses`, { pause_type_id: this.selectedTypeId })
        .then((r) => {
          this.current = r.data.data;
          this.$bvModal.hide("modal-pause-start");
          this.$message(null, this.$t("pause.started"), "success");
        })
        .catch((e) => {
          this.$message(this.$t("common.error"), this.apiErrorText(e), "error");
          // O saldo pode ter mudado (limite atingido em outra aba): mostra o estado atual.
          this.selectedTypeId = null;
          return this.loadTypes();
        })
        .finally(() => {
          this.busy = false;
        });
    },
    back() {
      this.busy = true;
      return this.$http
        .post(`${this.$store.state.api}pauses/end`)
        .then(() => {
          this.current = null;
          this.$message(null, this.$t("pause.ended"), "success");
        })
        .catch((e) => {
          this.$message(this.$t("common.error"), this.apiErrorText(e), "error");
          return this.loadCurrent();
        })
        .finally(() => {
          this.busy = false;
        });
    },
    subscribe(userId) {
      if (typeof window.Echo === "undefined") return;

      this.channelName = `user.${userId}`;
      this.listener = (payload) => {
        const hadPause = !!this.current;
        this.loadCurrent().then(() => {
          if (hadPause && !this.current && payload && payload.reason === "supervisor") {
            this.$message(null, this.$t("pause.endedBySupervisor"), "warning");
          }
        });
      };
      window.Echo.private(this.channelName).listen(EVENT, this.listener);
    },
    unsubscribe() {
      if (typeof window.Echo === "undefined" || !this.listener) return;

      // stopListening e não leave(): o canal do usuário também é usado por outros avisos.
      window.Echo.private(this.channelName).stopListening(EVENT, this.listener);
      this.listener = null;
      this.channelName = null;
    },
  },
};
</script>

<style scoped>
.pause-pill {
  position: relative;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px 10px 16px 10px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.96);
  color: #3d3d3d;
}

.pause-pill__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(40, 53, 131, 0.12);
  color: #283583;
}

.pause-pill__info {
  display: flex;
  flex-direction: column;
  min-width: 0;
  max-width: 150px;
  line-height: 1.2;
}

.pause-pill__status {
  font-size: 0.62rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  opacity: 0.6;
}

.pause-pill__name {
  overflow: hidden;
  font-size: 0.85rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pause-pill__clock {
  display: flex;
  flex-direction: column;
  align-items: center;
  line-height: 1.15;
}

.pause-pill__clock + .pause-pill__clock {
  padding-left: 16px;
  border-left: 1px solid rgba(130, 134, 139, 0.3);
}

.pause-pill__value {
  font-size: 1.05rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.pause-pill__clock--main .pause-pill__value {
  font-size: 1.3rem;
  color: #283583;
}

.pause-pill__caption {
  font-size: 0.6rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  opacity: 0.6;
}

/* Trilha solta, recuada das bordas: encostar na borda a cortava pelo arredondamento. */
.pause-pill__progress {
  position: absolute;
  right: 14px;
  bottom: 6px;
  left: 14px;
  height: 4px;
  overflow: hidden;
  border-radius: 999px;
  background: rgba(130, 134, 139, 0.2);
}

.pause-pill__progress span {
  display: block;
  height: 100%;
  border-radius: 999px;
  background: #283583;
  transition: width 1s linear;
}

.pause-pill--warning .pause-pill__clock--main .pause-pill__value {
  color: #e8750a;
}

.pause-pill--warning .pause-pill__progress span {
  background: #ff9f43;
}

.pause-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 320px;
  overflow-y: auto;
}

.pause-option {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 0.7rem 0.9rem;
  border: 1px solid rgba(130, 134, 139, 0.35);
  border-radius: 0.5rem;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s, background-color 0.15s;
}

.pause-option:hover:not(:disabled) {
  border-color: #283583;
}

.pause-option:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pause-option--selected {
  border-color: #283583;
  background: rgba(40, 53, 131, 0.08);
}

.pause-option__radio {
  flex: none;
  width: 18px;
  height: 18px;
  border: 2px solid rgba(130, 134, 139, 0.6);
  border-radius: 50%;
}

.pause-option--selected .pause-option__radio {
  border-color: #283583;
  background: radial-gradient(#283583 45%, transparent 50%);
}

.pause-option__text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.idle-pill {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 999px;
  background: #ff9f43;
  color: #fff;
  font-weight: 700;
}

.idle-pill__label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.idle-pill__time {
  font-variant-numeric: tabular-nums;
}

.pause-pill--overdue {
  background: #ea5455;
  color: #fff;
  animation: pause-pulse 1.4s ease-in-out infinite;
}

.pause-pill--overdue .pause-pill__icon {
  background: rgba(255, 255, 255, 0.25);
  color: #fff;
}

.pause-pill--overdue .pause-pill__clock--main .pause-pill__value {
  color: #fff;
}

.pause-pill--overdue .pause-pill__clock + .pause-pill__clock {
  border-left-color: rgba(255, 255, 255, 0.4);
}

.pause-pill--overdue .pause-pill__progress {
  background: rgba(255, 255, 255, 0.3);
}

.pause-pill--overdue .pause-pill__progress span {
  background: #fff;
}

@keyframes pause-pulse {
  50% {
    box-shadow: 0 0 0 5px rgba(234, 84, 85, 0.35);
  }
}

@media (prefers-reduced-motion: reduce) {
  .pause-pill--overdue {
    animation: none;
  }
}
</style>
